import React, {
  Dispatch,
  MutableRefObject,
  RefObject,
  SetStateAction,
  useMemo,
} from 'react'
import './InlineTextEditor.css'
import useInlineTextEditor, { IEditorInput } from './useInlineTextEditor'
import {
  Count,
  EmojiPicker,
  HtmlEditor,
  Inject,
  Link,
  QuickToolbar,
  RichTextEditorComponent,
  Toolbar,
  ToolbarSettingsModel,
} from '@syncfusion/ej2-react-richtexteditor'
import {
  CHAT_LEFT_BUTTONS,
  CHAT_RIGHT_BUTTONS,
  AI_CHAR_LIMIT,
} from '@constants/Message'
import { MentionComponent } from '@syncfusion/ej2-react-dropdowns'
import { generateSvg } from '@utils/svg.util'
import { Button } from '@components/common'
import { VARIABLE_LIST } from '@constants/Marketing'

export interface IInlineTEProps {
  /** Focus this composer when the selected conversation changes. */
  autoFocusKey?: string
  aiAction: string
  editorInput: IEditorInput
  enableEnterNextLine?: boolean
  enableMention?: boolean
  enableVariable?: boolean
  defaultPlaceholder?: string
  isAiEnabled: boolean
  setAiAction?: Dispatch<SetStateAction<string>>
  setEditorInput: Dispatch<SetStateAction<IEditorInput>>
  setIsAiEnabled: Dispatch<SetStateAction<boolean>>
  handleSend: () => void
  hideToolbar?: boolean
  isEditing?: boolean
  isEmojiEnabled?: boolean
  isLinkEnabled?: boolean
  isMentionEnabled?: boolean
  isVariableEnabled?: boolean
  onSaveEdit?: () => void
  resize?: boolean
  setIsEditing?: Dispatch<SetStateAction<boolean>>
  selectionRef?: MutableRefObject<Range | null>
  toolbarRef?: RefObject<RichTextEditorComponent>
  _variableList?: { text: string; value: string }[]
  _variableRef?: RefObject<MentionComponent>
}

const InlineTextEditor: React.FC<IInlineTEProps> = ({
  autoFocusKey,
  aiAction,
  editorInput,
  enableEnterNextLine = false,
  enableMention = true,
  enableVariable = true,
  defaultPlaceholder = 'Type a message',
  isAiEnabled,
  setAiAction,
  setEditorInput,
  setIsAiEnabled,
  handleSend,
  hideToolbar = false,
  isEditing,
  isEmojiEnabled = true,
  isLinkEnabled = true,
  isMentionEnabled = true,
  isVariableEnabled = true,
  onSaveEdit,
  resize = false,
  selectionRef,
  setIsEditing,
  toolbarRef,
  _variableList,
  _variableRef,
}) => {
  const { ...hooks } = useInlineTextEditor(
    editorInput,
    enableEnterNextLine,
    hideToolbar,
    isAiEnabled,
    setEditorInput,
    setIsAiEnabled,
    handleSend,
    toolbarRef ?? null,
    isEditing,
    onSaveEdit,
    setIsEditing,
    selectionRef,
    setAiAction,
    autoFocusKey,
  )

  const filteredButtons = useMemo(() => {
    return CHAT_LEFT_BUTTONS.filter((button) => {
      switch (button.key) {
        case 'ai':
          return true
        case 'emoji':
          return isEmojiEnabled
        case 'url':
          return isLinkEnabled
        case 'mention':
          return isMentionEnabled
        case 'variable':
          return isVariableEnabled
      }
      return false
    })
  }, [isEmojiEnabled, isLinkEnabled, isMentionEnabled, isVariableEnabled])

  const toolbarSettings: ToolbarSettingsModel = {
    items: filteredButtons.map((data) => {
      if (data.type === 'builtin') {
        return data.icon
      } else {
        return {
          template: generateSvg(
            data.key,
            data.icon,
            data.type === 'ti' ? 24 : 16,
          ),
          undo: true,
          tooltipText: data.text,
          click: () => {
            hooks.handleToolbarActions(data.key)
          },
        }
      }
    }),
    enable: !hideToolbar,
  }
  const injectedServices = useMemo(
    () => [Toolbar, Link, HtmlEditor, QuickToolbar, EmojiPicker, Count],
    [],
  )

  return (
    <>
      <div
        className={`inline-editor-container ${hooks.isRecording ? 'disabled' : ''}`}
      >
        {isAiEnabled && (
          <div className="ai-container">
            <div className="ai-buttons">
              <button
                type="button"
                className={`unstyled-button ai-button${aiAction === 'Refine' ? ' active' : ''}`}
                onClick={() => {
                  if (aiAction === 'Refine') {
                    setAiAction?.('custom')
                  } else {
                    setAiAction?.('Refine')
                  }
                }}
              >
                <span className="mgc_pencil_2_ai_fill ab-icon" />
                Refine
              </button>
              <button
                type="button"
                className={`unstyled-button ai-button${aiAction === 'Variables' ? ' active' : ''}`}
                onClick={() => {
                  if (aiAction === 'Variables') {
                    setAiAction?.('custom')
                  } else {
                    setAiAction?.('Variables')
                  }
                }}
              >
                <span className="ti ti-variable ab-icon" />
                Variables
                <span className="ti ti-triangle-inverted-filled caret" />
              </button>
            </div>
          </div>
        )}
        {isAiEnabled && aiAction === 'Variables' && (
          <div className="ai-variable-container">
            {VARIABLE_LIST.map((data) => (
              <div key={data.value} className="ai-button">
                {data.name}
              </div>
            ))}
          </div>
        )}
        {hooks.isRecording && (
          <div className="mcc-audio-rec">
            <span className="mgc_mic_fill" /> 00:00
          </div>
        )}
        <div className="editor-wrapper">
          {isAiEnabled && (
            <div className="ai-inline-display">
              <Button
                size="sm"
                className="button-secondary"
                leftIcon={<span className="mgc_magic_3_fill" />}
                rightIcon={<span className="mgc_close_line" />}
                onClick={hooks.handleClearBtn}
              />
              <span>/AI</span>
            </div>
          )}
          <RichTextEditorComponent
            key={isAiEnabled ? 'rte-char-count' : 'rte-no-char-count'}
            id="inline-target-input"
            ref={hooks.toolbarRef}
            toolbarSettings={toolbarSettings}
            actionBegin={hooks.onActionBegin}
            value={editorInput.htmlContent}
            enableTabKey={true}
            enterKey="BR"
            cssClass="message-rte-tooltip"
            className={isAiEnabled ? 'glow' : 'default'}
            placeholder={
              !isAiEnabled
                ? defaultPlaceholder
                : aiAction === 'Refine'
                  ? 'Type here to paraphrase with AI'
                  : 'Ask AI anything'
            }
            style={resize ? { resize: 'vertical' } : undefined}
            showCharCount={isAiEnabled}
            maxLength={isAiEnabled ? AI_CHAR_LIMIT : undefined}
            change={hooks.onTextChange}
            created={hooks.onCreated}
            onClick={hooks.saveCaretPosition}
            focus={hooks.saveCaretPosition}
          >
            <Inject services={injectedServices} />
          </RichTextEditorComponent>
          {!hideToolbar && (
            <div className="chat-right-buttons">
              {CHAT_RIGHT_BUTTONS.map((data) => (
                <button
                  type="button"
                  key={data.key}
                  data-bs-toggle="tooltip"
                  title={data.text}
                  aria-label={data.text}
                  id={`custom-${data.key}-button`}
                  className={`unstyled-button crb-button crb-${data.key}-button ${hooks.isRecording ? 'active' : ''}`}
                  onClick={() => {
                    hooks.handleRightButtonsAction(data.key)
                  }}
                >
                  <span className={`${data.icon} ${data.key}-icon rb-icon`} />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="tec-mention-comp">
        {enableVariable && (
          <MentionComponent
            target="#inline-target-input"
            ref={_variableRef ?? hooks.variableRef}
            dataSource={_variableList ?? VARIABLE_LIST}
            showMentionChar={false}
            mentionChar="("
            cssClass="variable-component-text"
            fields={{ text: 'text', value: 'value' }}
            suffixText={'&nbsp;'}
            opened={() => hooks.setVariablePopupOpen(true)}
            closed={() => hooks.setVariablePopupOpen(false)}
          />
        )}
        {enableMention && (
          <MentionComponent
            target="#inline-target-input"
            ref={hooks.mentionRef}
            dataSource={hooks.peopleList}
            showMentionChar={true}
            mentionChar="@"
            cssClass="mention-component-text"
            fields={{ text: 'name', value: 'name' }}
            suffixText={'&nbsp;'}
          />
        )}
      </div>
    </>
  )
}

export default InlineTextEditor
